import { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { userPreferencesService } from '../../../services/userPreferencesService';
import { ColumnConfig as BaseColumnConfig } from './types';
import { getGetUsersIdQueryKey } from '../../../api/generated/arrayscapeGamingDashboardAPI';
import type { User } from '../../../api/generated/model';

/**
 * Extended column config with management-specific properties
 */
export interface ManagedColumnConfig<K extends string = string> extends BaseColumnConfig {
  key: K;
  label: string;
  sortable: boolean;
  width: number;
  visible: boolean;
}

/**
 * User preferences structure for column management
 */
export interface ColumnPreferences<K extends string = string> {
  table_column_orders?: {
    [tableName: string]: Array<{ key: K; visible: boolean }>;
  };
  table_column_widths?: {
    [tableName: string]: Record<K, number>;
  };
}

/**
 * Hook configuration options
 */
export interface UseColumnManagementOptions<K extends string = string> {
  /** Default column configuration */
  defaultColumns: ManagedColumnConfig<K>[];

  /** Unique key for saving preferences (e.g., 'events', 'customers') */
  preferencesKey?: string;

  /** User ID for saving preferences */
  userId?: string;

  /** User preferences data from API */
  userPreferences?: ColumnPreferences<K>;

  /** Whether column reorder is enabled (affects min width calculation) */
  enableColumnReorder?: boolean;
}

/**
 * Resize state and handlers
 */
export interface ResizeHandlers {
  /** Column currently being resized (actual index in columnOrder) */
  resizingColumn: number | null;

  /** Start resizing a column */
  handleResizeStart: (e: React.MouseEvent, visibleIndex: number) => void;

  /** Get minimum width for a column based on header content */
  calculateMinWidth: (columnIndex: number) => number;

  /** Ref to store header cell elements for width calculation */
  headerRefs: React.MutableRefObject<Map<number, HTMLTableCellElement>>;
}

/**
 * Drag and drop state and handlers
 */
export interface DragHandlers {
  /** Index of column being dragged (visible index) */
  draggedColumn: number | null;

  /** Start dragging a column */
  handleDragStart: (e: React.DragEvent, visibleIndex: number) => void;

  /** Handle drag over event */
  handleDragOver: (e: React.DragEvent) => void;

  /** Handle drop event */
  handleDrop: (e: React.DragEvent, dropVisibleIndex: number) => void;
}

/**
 * Hook return value
 */
export interface UseColumnManagementReturn<K extends string = string> {
  /** Current column order with visibility and width settings */
  columnOrder: ManagedColumnConfig<K>[];

  /** Update column order */
  setColumnOrder: React.Dispatch<React.SetStateAction<ManagedColumnConfig<K>[]>>;

  /** Resize handlers */
  resize: ResizeHandlers;

  /** Drag and drop handlers */
  drag: DragHandlers;

  /** Toggle column visibility */
  toggleColumnVisibility: (columnKey: K) => Promise<void>;

  /** Reset to default column configuration */
  resetToDefaults: () => Promise<void>;

  /** Check if currently loading preferences */
  isLoadingPreferences: boolean;

  /** Flag indicating if a column was just resized (prevents sort on click) */
  justResized: React.MutableRefObject<boolean>;
}

/**
 * Reusable hook for managing table column state including:
 * - Column reordering via drag and drop
 * - Column resizing with min-width constraints
 * - Column visibility toggling
 * - Persistence to user preferences
 *
 * @example
 * ```tsx
 * const { columnOrder, resize, drag, toggleColumnVisibility } = useColumnManagement({
 *   defaultColumns: myColumns,
 *   preferencesKey: 'events',
 *   userId: user?.id,
 *   userPreferences: userData?.preferences
 * });
 * ```
 */
export function useColumnManagement<K extends string = string>(
  options: UseColumnManagementOptions<K>
): UseColumnManagementReturn<K> {
  const { defaultColumns, preferencesKey, userId, userPreferences, enableColumnReorder = true } = options;
  const queryClient = useQueryClient();

  // Column state
  const [columnOrder, setColumnOrder] = useState<ManagedColumnConfig<K>[]>(defaultColumns);

  // Drag state
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);

  // Resize state
  const [resizingColumn, setResizingColumn] = useState<number | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number>(0);
  const resizeInitialWidth = useRef<number>(0);
  const resizeMinWidth = useRef<number>(60);
  const justResized = useRef<boolean>(false);

  // Refs for width calculations
  const headerRefs = useRef<Map<number, HTMLTableCellElement>>(new Map());
  const hasLoadedPreferences = useRef<boolean>(false);

  /**
   * Calculate minimum width for a column based on its header content
   */
  const calculateMinWidth = useCallback((columnIndex: number): number => {
    const headerCell = headerRefs.current.get(columnIndex);
    if (!headerCell) return 60; // fallback

    // Find the content div (the one with d-flex class)
    const contentDiv = headerCell.querySelector('.d-flex') as HTMLElement;
    if (!contentDiv) return 60;

    // Get the column config to access the label
    const column = columnOrder[columnIndex];
    const labelSpan = contentDiv.querySelector('span');
    if (!labelSpan) return 60;

    // Split label into words and measure the widest word
    const words = column.label.split(' ');
    let maxWordWidth = 0;

    // Create a hidden element to measure word widths
    const measureDiv = document.createElement('div');
    measureDiv.style.position = 'absolute';
    measureDiv.style.visibility = 'hidden';
    measureDiv.style.whiteSpace = 'nowrap';
    measureDiv.style.left = '-9999px';

    // Copy font styles from the label span
    const computedStyle = window.getComputedStyle(labelSpan);
    measureDiv.style.font = computedStyle.font;
    measureDiv.style.fontSize = computedStyle.fontSize;
    measureDiv.style.fontWeight = computedStyle.fontWeight;
    measureDiv.style.fontFamily = computedStyle.fontFamily;

    document.body.appendChild(measureDiv);

    // Measure each word
    for (const word of words) {
      measureDiv.textContent = word;
      maxWordWidth = Math.max(maxWordWidth, measureDiv.offsetWidth);
    }

    document.body.removeChild(measureDiv);

    // Add width for icons and gaps:
    // - grip icon: ~12px
    // - gap after grip: 4px
    // - gap/margin before sort icon: 4px
    // - sort icon (if sortable): ~16px
    const gripIconWidth = enableColumnReorder ? 12 : 0;
    const gapWidth = enableColumnReorder ? 8 : 0;
    const sortIconWidth = column.sortable ? 16 : 0;
    const padding = 16;

    const totalWidth = gripIconWidth + gapWidth + maxWordWidth + sortIconWidth + padding;

    // Ensure minimum of 30px
    return Math.max(30, totalWidth + 20);
  }, [columnOrder]);

  /**
   * Handle mouse move during resize
   */
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (resizingColumn === null) return;

    const delta = e.clientX - resizeStartX;
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const newWidth = Math.max(resizeMinWidth.current, resizeInitialWidth.current + delta);
      newOrder[resizingColumn] = { ...newOrder[resizingColumn], width: newWidth };
      return newOrder;
    });
  }, [resizingColumn, resizeStartX]);

  /**
   * Handle mouse up during resize (save preferences)
   */
  const handleResizeEnd = useCallback(async () => {
    if (resizingColumn === null) return;

    setResizingColumn(null);
    justResized.current = true;
    setTimeout(() => {
      justResized.current = false;
    }, 100);

    // Save to preferences if configured
    if (userId && preferencesKey) {
      // Use functional state update to get the latest column order
      // This avoids race condition where final setColumnOrder from handleResizeMove
      // hasn't completed yet when handleResizeEnd is called
      setColumnOrder(currentOrder => {
        const columnWidths = currentOrder.reduce((acc, col) => {
          acc[col.key] = col.width;
          return acc;
        }, {} as Record<K, number>);

        // Update React Query cache immediately so navigation preserves the new widths
        const queryKey = getGetUsersIdQueryKey(userId);
        queryClient.setQueryData<User>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            preferences: {
              ...oldData.preferences,
              table_column_widths: {
                ...oldData.preferences?.table_column_widths,
                [preferencesKey]: columnWidths
              }
            }
          };
        });

        // Save to backend
        userPreferencesService.updateTableColumnWidths(userId, preferencesKey, columnWidths)
          .catch(error => {
            console.error(`Failed to save ${preferencesKey} column widths:`, error);
          });

        return currentOrder; // Don't modify state, just use it to get latest value
      });
    }
  }, [resizingColumn, userId, preferencesKey, queryClient]);

  /**
   * Attach/detach resize event listeners
   */
  useEffect(() => {
    if (resizingColumn !== null) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);

      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  /**
   * Start resizing a column
   */
  const handleResizeStart = useCallback((e: React.MouseEvent, visibleIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    // Map visible index to actual column order index
    const visibleColumns = columnOrder.filter(col => col.visible);
    const column = visibleColumns[visibleIndex];
    const actualIndex = columnOrder.findIndex(col => col.key === column.key);

    setResizingColumn(actualIndex);
    setResizeStartX(e.clientX);
    resizeInitialWidth.current = columnOrder[actualIndex].width;
    resizeMinWidth.current = calculateMinWidth(actualIndex);
  }, [columnOrder, calculateMinWidth]);

  /**
   * Start dragging a column
   */
  const handleDragStart = useCallback((e: React.DragEvent, visibleIndex: number) => {
    setDraggedColumn(visibleIndex);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * Handle dropping a column (reorder and save)
   */
  const handleDrop = useCallback(async (e: React.DragEvent, dropVisibleIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropVisibleIndex) return;

    // Map visible column indices to full column order indices
    const visibleColumns = columnOrder.filter(col => col.visible);
    const draggedVisibleColumn = visibleColumns[draggedColumn];
    const dropVisibleColumn = visibleColumns[dropVisibleIndex];

    const actualDraggedIndex = columnOrder.findIndex(col => col.key === draggedVisibleColumn.key);
    const actualDropIndex = columnOrder.findIndex(col => col.key === dropVisibleColumn.key);

    const newColumnOrder = [...columnOrder];
    const draggedItem = newColumnOrder[actualDraggedIndex];

    newColumnOrder.splice(actualDraggedIndex, 1);
    newColumnOrder.splice(actualDropIndex, 0, draggedItem);

    setColumnOrder(newColumnOrder);
    setDraggedColumn(null);

    // Save to preferences if configured
    if (userId && preferencesKey) {
      try {
        const columnConfigs = newColumnOrder.map(col => ({ key: col.key, visible: col.visible }));

        // Update React Query cache immediately
        const queryKey = getGetUsersIdQueryKey(userId);
        queryClient.setQueryData<User>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            preferences: {
              ...oldData.preferences,
              table_column_orders: {
                ...oldData.preferences?.table_column_orders,
                [preferencesKey]: columnConfigs
              }
            }
          };
        });

        await userPreferencesService.updateTableColumnOrder(userId, preferencesKey, columnConfigs);
      } catch (error) {
        console.error(`Failed to save ${preferencesKey} column order:`, error);
      }
    }
  }, [draggedColumn, columnOrder, userId, preferencesKey, queryClient]);

  /**
   * Toggle column visibility and save to preferences
   */
  const toggleColumnVisibility = useCallback(async (columnKey: K) => {
    const newOrder = columnOrder.map(col =>
      col.key === columnKey ? { ...col, visible: !col.visible } : col
    );

    setColumnOrder(newOrder);

    // Save to preferences if configured
    if (userId && preferencesKey) {
      try {
        const columnConfigs = newOrder.map(col => ({ key: col.key, visible: col.visible }));

        // Update React Query cache immediately
        const queryKey = getGetUsersIdQueryKey(userId);
        queryClient.setQueryData<User>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            preferences: {
              ...oldData.preferences,
              table_column_orders: {
                ...oldData.preferences?.table_column_orders,
                [preferencesKey]: columnConfigs
              }
            }
          };
        });

        await userPreferencesService.updateTableColumnOrder(userId, preferencesKey, columnConfigs);
      } catch (error) {
        console.error(`Failed to save ${preferencesKey} column visibility:`, error);
      }
    }
  }, [columnOrder, userId, preferencesKey, queryClient]);

  /**
   * Reset columns to defaults and clear preferences
   */
  const resetToDefaults = useCallback(async () => {
    setColumnOrder([...defaultColumns]);

    // Clear saved preferences if configured
    if (userId && preferencesKey) {
      try {
        // Update React Query cache immediately
        const queryKey = getGetUsersIdQueryKey(userId);
        queryClient.setQueryData<User>(queryKey, (oldData) => {
          if (!oldData) return oldData;

          const newTableColumnOrders = { ...oldData.preferences?.table_column_orders };
          delete newTableColumnOrders[preferencesKey];

          const newTableColumnWidths = { ...oldData.preferences?.table_column_widths };
          delete newTableColumnWidths[preferencesKey];

          return {
            ...oldData,
            preferences: {
              ...oldData.preferences,
              table_column_orders: newTableColumnOrders,
              table_column_widths: newTableColumnWidths
            }
          };
        });

        // Clear column order/visibility
        await userPreferencesService.updateTableColumnOrder(userId, preferencesKey, []);
        // Clear column widths
        await userPreferencesService.updateTableColumnWidths(userId, preferencesKey, {});
      } catch (error) {
        console.error(`Failed to reset ${preferencesKey} table preferences:`, error);
      }
    }
  }, [defaultColumns, userId, preferencesKey, queryClient]);

  /**
   * Load saved column order, widths, and visibility from user preferences
   * Only loads once on mount - subsequent changes to preferences won't reload
   */
  useEffect(() => {
    // Only load preferences once per preferencesKey
    if (hasLoadedPreferences.current || !userPreferences || !preferencesKey) {
      return;
    }

    hasLoadedPreferences.current = true;

    let newOrder = [...defaultColumns];

    // Apply saved column order and visibility
    const savedConfigs = userPreferences.table_column_orders?.[preferencesKey];
    if (savedConfigs && savedConfigs.length > 0) {
      // Create a map of saved configs for quick lookup
      const savedConfigMap = new Map(savedConfigs.map(config => [config.key, config.visible]));

      // Build ordered list from saved configs
      const orderedColumns = savedConfigs
        .map(config => {
          const defaultCol = defaultColumns.find(col => col.key === config.key);
          if (defaultCol) {
            return { ...defaultCol, visible: config.visible };
          }
          return null;
        })
        .filter(Boolean) as ManagedColumnConfig<K>[];

      // Add any new columns that aren't in saved preferences yet
      const savedKeys = new Set(savedConfigs.map(config => config.key));
      const newColumns = defaultColumns.filter(col => !savedKeys.has(col.key));

      newOrder = [...orderedColumns, ...newColumns];
    }

    // Apply saved column widths
    const savedWidths = userPreferences.table_column_widths?.[preferencesKey] as Record<K, number> | undefined;
    if (savedWidths && Object.keys(savedWidths).length > 0) {
      newOrder = newOrder.map(col => ({
        ...col,
        width: savedWidths[col.key] || col.width
      }));
    }

    setColumnOrder(newOrder);
  }, [preferencesKey, userPreferences, defaultColumns]);

  /**
   * Ensure columns meet minimum width based on header content after render
   * This runs once when the table first renders with data
   */
  useEffect(() => {
    if (headerRefs.current.size === 0) return;

    // Check if any columns need to be widened to fit their headers
    const needsUpdate = columnOrder.some((col, index) => {
      const minWidth = calculateMinWidth(index);
      return col.width < minWidth;
    });

    if (needsUpdate) {
      setColumnOrder(prev => prev.map((col, index) => {
        const minWidth = calculateMinWidth(index);
        if (col.width < minWidth) {
          return { ...col, width: minWidth };
        }
        return col;
      }));
    }
  }, [columnOrder.length, calculateMinWidth]); // Run when table first renders

  return {
    columnOrder,
    setColumnOrder,
    resize: {
      resizingColumn,
      handleResizeStart,
      calculateMinWidth,
      headerRefs,
    },
    drag: {
      draggedColumn,
      handleDragStart,
      handleDragOver,
      handleDrop,
    },
    toggleColumnVisibility,
    resetToDefaults,
    isLoadingPreferences: false, // Always false now since we load once on mount
    justResized,
  };
}
